<script setup>
import { ref, computed, onMounted } from 'vue'
import { useGlobalStore } from '@stores/global'
import { useEventStore } from '@stores/event'
import _ from 'lodash'
import Loading from '@components/global/Loading.vue'
import Hero from '@sections/global/Hero.vue'
import ContentFrame from '@components/global/ContentFrame.vue'
import ContentSection from '@components/global/ContentSection.vue'
import Input from '@components/forms/Input.vue'
import Textarea from '@components/forms/Textarea.vue'
import Button from '@components/forms/Button.vue'
import Datetime from '@components/forms/Datetime.vue'
import Select from '@components/forms/Select.vue'
import Toggle from '@components/forms/Toggle.vue'
import EventCard from '@components/index/EventCard.vue'
import TransitionGrid from '@components/global/transitions/TransitionGrid.vue'
import TransitionFade from '@components/global/transitions/TransitionFade.vue'

const global = useGlobalStore()
const event = useEventStore()
const date = ref(new Date())
const upcomingExpanded = ref(true)
const completedExpanded = ref(true)

const views = ref([
	{
		name: 'List',
		icon: 'fas fa-list',
		slug: 'list',
	},
	{
		name: 'Grid',
		icon: 'fas fa-grid',
		slug: 'grid',
	},
])
const filters = ref({})
const view = ref('list')

const filteredEvents = computed(() => {
	return event.events.filter((event) => {
		const qualifierMatch = !filters.value.qualifier || filters.value.qualifier.length === 0 || (event.qualifier_for ?? []).some((qualifier) => filters.value.qualifier.includes(qualifier.slug))
		const typeMatch = !filters.value.type || filters.value.type.length === 0 || filters.value.type.includes(event.type)
		const gamesMatch = !filters.value.game || filters.value.game.length === 0 || event.games.some((game) => filters.value.game.includes(game.slug))
		const countryMatch = !filters.value.country || filters.value.country.length === 0 || filters.value.country.includes(event.country?.slug)
		const dateMatch = !filters.value.dateRange || filters.value.dateRange.length === 0 || (event.start_at >= filters.value.dateRange[0] && event.start_at <= filters.value.dateRange[1])
		return qualifierMatch && typeMatch && gamesMatch && countryMatch && dateMatch
	})
})
const upcomingEvents = computed(() => {
	return (
		_.orderBy(
			filteredEvents.value.filter((event) => event.start_at && event.completed == false),
			'start_at',
			'asc',
		) ?? []
	)
})
const completedEvents = computed(() => {
	return filteredEvents.value.filter((event) => event.start_at && event.completed == true) ?? []
})

const clear = () => {
	filters.value = {}
}
</script>

<template>
	<Loading v-if="event.events?.length === 0" />
	<template v-else>
		<Hero title="Qualifying Tournaments" />
		<section>
			<div class="container">
				<div class="relative flex items-center gap-3">
					<!-- <div class="hidden items-center gap-3 sm:flex">
						<Toggle :options="views" v-model="view" />
						<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					</div> -->
					<div class="flex grow flex-col gap-3 md:flex-row">
						<Select class="secondary" :options="event.qualifiers" v-model="filters.qualifier" placeholder="Qualifier" />
						<Select class="secondary" :options="event.types" v-model="filters.type" placeholder="Type" />
						<Select class="secondary" :options="event.games" v-model="filters.game" placeholder="Game" />
						<Select class="secondary" :options="event.countries" v-model="filters.country" placeholder="Country" />
						<Datetime class="secondary" v-model="filters.dateRange" placeholder="Dates" :time-config="{ enableTimePicker: false }" :auto-apply="true" timezone="UTC" :min-date="new Date()" :range="true" />
					</div>
					<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					<div class="flex gap-3">
						<Button class="secondary" @click="clear">Clear</Button>
					</div>
				</div>
			</div>
		</section>
		<section class="my-10">
			<div class="container flex flex-col gap-5">
				<div class="flex flex-col gap-16">
					<div class="flex flex-col gap-5" v-if="upcomingEvents.length > 0">
						<div class="flex items-center justify-between">
							<h6>Upcoming</h6>
							<button class="flex items-center justify-center p-2" @click="upcomingExpanded = !upcomingExpanded"><i :class="['fas fa-chevron-up transition-transform duration-300', { 'rotate-180': upcomingExpanded }]"></i></button>
						</div>
						<div class="flex flex-col gap-2" v-if="upcomingExpanded">
							<EventCard v-for="event in upcomingEvents" :event="event" :key="event.slug" />
						</div>
					</div>
					<div class="flex flex-col gap-5" v-if="completedEvents.length > 0">
						<div class="flex items-center justify-between">
							<h6>Completed</h6>
							<button class="flex items-center justify-center p-2" @click="completedExpanded = !completedExpanded"><i :class="['fas fa-chevron-up transition-transform duration-300', { 'rotate-180': completedExpanded }]"></i></button>
						</div>
						<div class="flex flex-col gap-5" v-if="completedExpanded">
							<EventCard v-for="event in completedEvents" :event="event" :key="event.slug" />
						</div>
					</div>
				</div>
			</div>
		</section>
	</template>
</template>

<style scoped>
@reference '../../css/theme.css';

.list-leave-active {
	@apply absolute;
}
.grid-leave-active {
	@apply absolute;
}
</style>
